<template>
  <div class="discount-rule-editor responsive-menu">
    <div class="responsive-menu__child">
      <PageHeader
        :pageHeader="pageHeader"
        :links="[
          { name: 'home', label: 'Home' },
          { name: 'discountRules', label: 'Discount Rules' },
          {
            name: 'discountRuleEditor',
            label: selectedRule.name || 'New Rule',
            query: $route.query,
          },
        ]"
        :canEdit="!selectedRule.isDeleted"
        :isEdit="isEdit"
        :isCollapsed="isCollapsed"
        :selectedEntity="selectedRule"
        @updateIsEdit="isEdit = $event"
        :entity="entity"
        @toggle-edit="toggleEdit"
        @toggle-sections="forceToggle"
        @handle-adjacent="handleAdjacent"
        @show-search-modal="toggleSearchModal"
        @menu-option-click="menuOptionClick"
      >
        <BaseBtn
          v-if="selectedRule._id !== 'NEW' && !isEdit"
          label="Reset Usages"
          iconGroup="fas"
          iconName="undo"
          :colorType="4"
          @click="confirmResetUsages"
        />
      </PageHeader>

      <div class="section-base">
        <!-- Basic Info Section -->
        <div
          style="position: relative"
          class="padded elevated-0 bordered"
          :class="{
            'bordered__highlighted--danger': selectedRule.isDeleted || selectedRule.isArchived,
          }"
        >
          <transition name="fade">
            <ContainerLabel v-if="selectedRule.isDeleted" type="danger" text="DELETED" />
          </transition>

          <Input
              v-if="enforceMso"
              :key="true"
              :model="selectedRule.msoDiscountRule"
              :inputData="getInput('msoDiscountRule')"
              :isEdit="isEdit"
              @updateModel="(event) => setValue('msoDiscountRule', event)"
          />

          <div class="discount-rule-editor__grid">
            <div class="discount-rule-editor__grid__column">
              <Input
                :model="selectedRule.name"
                :inputData="getInput('name')"
                :isEdit="isFieldEditable"
                @updateModel="(event) => setValue('name', event)"
              />
              <Input
                :model="selectedRule.discountType"
                :inputData="getInput('discountType')"
                :isEdit="isFieldEditable"
                @updateModel="(event) => setValue('discountType', event)"
              />
              <Input
                v-if="selectedRule.discountType === 'coupon'"
                :model="selectedRule.code"
                :inputData="getInput('code')"
                :isEdit="isFieldEditable"
                @updateModel="(event) => setValue('code', event)"
              />
            </div>

            <div class="discount-rule-editor__grid__column">
              <Input
                :model="selectedRule.saleType"
                :inputData="getInput('saleType')"
                :isEdit="isFieldEditable"
                @updateModel="(event) => setValue('saleType', event)"
              />
              <Input
                :model="selectedRule.isActive"
                :inputData="getInput('isActive')"
                :isEdit="isFieldEditable"
                @updateModel="(event) => setValue('isActive', event)"
              />
              <!-- Source Type Checkboxes -->
              <div class="discount-rule-editor__checkbox-group">
                <label class="discount-rule-editor__checkbox-label">Source</label>
                <div class="discount-rule-editor__checkbox-options">
                  <Input
                    v-for="opt in getSourceTypeInput()"
                    :key="opt.id"
                    :model="selectedRule.sourceType.includes(opt.value)"
                    :inputData="opt"
                    :isEdit="isFieldEditable"
                    @updateModel="toggleSourceType(opt.value)"
                  />
                </div>
              </div>
            </div>
          </div>

          <Input
            :model="selectedRule.onlineStoreName"
            :inputData="getInput('onlineStoreName')"
            :isEdit="isFieldEditable"
            @updateModel="(event) => setValue('onlineStoreName', event)"
          />

          <Input
            :model="selectedRule.description"
            :inputData="getInput('description')"
            :isEdit="isFieldEditable"
            @updateModel="(event) => setValue('description', event)"
          />
        </div>

        <!-- Schedule Section -->
        <EditorSection title="Schedule" subtitle="Define when this discount is active - set date/time range and specific days of the week." sectionType="schedule" :bus="bus">
          <div class="discount-rule-editor__schedule-container">
            <div class="discount-rule-editor__grid">
              <div class="discount-rule-editor__grid__column">
                <Input
                  :model="selectedRule.startDateTime"
                  :inputData="getScheduleInput('startDateTime')"
                  :isEdit="isEdit"
                  @updateModel="(event) => setValue('startDateTime', event)"
                />
              </div>
              <div class="discount-rule-editor__grid__column">
                <Input
                  :model="selectedRule.endDateTime"
                  :inputData="getScheduleInput('endDateTime')"
                  :isEdit="isEdit"
                  @updateModel="(event) => setValue('endDateTime', event)"
                />
              </div>
            </div>
            <div class="discount-rule-editor__checkbox-group">
              <label class="discount-rule-editor__checkbox-label">Days of Week</label>
              <div class="discount-rule-editor__checkbox-options discount-rule-editor__checkbox-options--inline">
                <Input
                    v-for="opt in getDayOfWeekInput()"
                    :key="opt.id"
                    :model="selectedRule.daysOfWeek.includes(opt.value)"
                    :inputData="opt"
                    :isEdit="isEdit"
                    @updateModel="toggleDayOfWeek(opt.value)"
                  />
              </div>
              <span class="discount-rule-editor__checkbox-hint">Leave all unchecked to apply every day</span>
            </div>
          </div>
        </EditorSection>

        <!-- Conditions Section (hidden for bundle type) -->
        <EditorSection
          v-if="selectedRule.discountType !== 'bundle'"
          title="Conditions"
          subtitle="Filter which products qualify for this discount based on group, brand, supplier, price, and more."
          sectionType="conditions"
          :bus="bus"
          :forceCollapsed="enforceMso"
        >
          <div class="discount-rule-editor__conditions-container">
            <ConditionBuilder
              v-if="selectedRule.conditions"
              :conditions="selectedRule.conditions"
              :isEdit="isFieldEditable"
              @update="selectedRule.conditions = $event"
            />
            <div v-else class="discount-rule-editor__empty-state">
              No conditions defined. This rule will apply to all items.
            </div>
          </div>
        </EditorSection>

        <!-- Bundle Groups Section (shown only for bundle type) -->
        <EditorSection
          v-if="selectedRule.discountType === 'bundle'"
          title="Bundle Groups"
          subtitle="Define groups of products that must all be in the cart to qualify for the bundle discount. Each group specifies conditions and required quantity."
          sectionType="bundleGroups"
          :bus="bus"
          :forceCollapsed="enforceMso"
        >
          <div class="discount-rule-editor__bundle-groups-container">
            <div
              v-for="(group, groupIndex) in selectedRule.bundleGroups"
              :key="'bundle-group-' + groupIndex"
              class="discount-rule-editor__bundle-group"
            >
              <div class="discount-rule-editor__bundle-group-header">
                <span class="discount-rule-editor__bundle-group-number">Group {{ groupIndex + 1 }}</span>
                <button
                  v-if="isFieldEditable"
                  type="button"
                  class="discount-rule-editor__bundle-group-remove"
                  @click="removeBundleGroup(groupIndex)"
                >
                  Remove
                </button>
              </div>

              <div class="discount-rule-editor__grid">
                <div class="discount-rule-editor__grid__column">
                  <Input
                    :model="group.label"
                    :inputData="{ id: 'bundleGroupLabel-' + groupIndex, type: 'input', title: 'Label', placeholder: 'e.g., Flowers, Edibles' }"
                    :isEdit="isFieldEditable"
                    @updateModel="(event) => updateBundleGroupField(groupIndex, 'label', event)"
                  />
                </div>
                <div class="discount-rule-editor__grid__column">
                  <Input
                    :model="group.requiredQuantity"
                    :inputData="{ id: 'bundleGroupQty-' + groupIndex, type: 'number', title: 'Required Quantity', precision: 0 }"
                    :isEdit="isFieldEditable"
                    @updateModel="(event) => updateBundleGroupField(groupIndex, 'requiredQuantity', event)"
                  />
                </div>
              </div>

              <div class="discount-rule-editor__bundle-group-conditions">
                <label class="discount-rule-editor__section-label">Group Conditions</label>
                <ConditionBuilder
                  v-if="group.conditions"
                  :conditions="group.conditions"
                  :isEdit="isFieldEditable"
                  @update="updateBundleGroupConditions(groupIndex, $event)"
                />
              </div>
            </div>

            <div v-if="!selectedRule.bundleGroups || selectedRule.bundleGroups.length === 0" class="discount-rule-editor__empty-state">
              No bundle groups defined. Add at least one group to configure the bundle.
            </div>

            <button
              v-if="isFieldEditable"
              type="button"
              class="discount-rule-editor__add-bundle-group-btn"
              @click="addBundleGroup"
            >
              + Add Bundle Group
            </button>

          </div>
        </EditorSection>

        <!-- Triggers Section -->
        <EditorSection title="Triggers" subtitle="Set cart-level thresholds (quantity, spend, cannabis weight) that must be met for the discount to apply." sectionType="triggers" :bus="bus" :forceCollapsed="enforceMso">
          <div class="discount-rule-editor__triggers-container">
            <div class="discount-rule-editor__grid">
              <div class="discount-rule-editor__grid__column">
                <Input
                  :model="selectedRule.triggers.minQuantity"
                  :inputData="getTriggerInput('triggers.minQuantity')"
                  :isEdit="isFieldEditable"
                  @updateModel="(event) => setValue('triggers.minQuantity', event)"
                />
                <Input
                  :model="selectedRule.triggers.minSpend"
                  :inputData="getTriggerInput('triggers.minSpend')"
                  :isEdit="isFieldEditable"
                  @updateModel="(event) => setValue('triggers.minSpend', event)"
                />
              </div>
              <div class="discount-rule-editor__grid__column">
                <Input
                  :model="selectedRule.triggers.minCannabisGrams"
                  :inputData="getTriggerInput('triggers.minCannabisGrams')"
                  :isEdit="isFieldEditable"
                  @updateModel="(event) => setValue('triggers.minCannabisGrams', event)"
                />
              </div>
            </div>

            <!-- Customer Conditions - Uses ConditionBuilder for flexibility -->
            <div class="discount-rule-editor__customer-conditions">
              <label class="discount-rule-editor__section-label">Customer Conditions</label>
              <ConditionBuilder
                v-if="selectedRule.triggers.customerConditions"
                :conditions="selectedRule.triggers.customerConditions"
                :isEdit="isFieldEditable"
                :fieldOptions="filteredCustomerConditionFieldOptions"
                @update="updateCustomerConditions($event)"
              />
            </div>
          </div>
        </EditorSection>

        <!-- Effects Section -->
        <EditorSection title="Effects" subtitle="Define the discount type: percent off, dollar off, fixed price, or free. Configure which items receive the discount." sectionType="effects" :bus="bus" :forceCollapsed="enforceMso">
          <div class="discount-rule-editor__effects-container">
            <EffectBuilder
              :effects="selectedRule.effects"
              :isEdit="isFieldEditable"
              :discountType="selectedRule.discountType"
              @update="selectedRule.effects = $event"
            />
          </div>
        </EditorSection>

        <!-- Stacking Section - Hidden for now -->
        <!-- <EditorSection title="Stacking Rules" subtitle="Control how this discount interacts with other discounts - stackable, exclusive, or custom rules." sectionType="stacking" :bus="bus">
          <div class="discount-rule-editor__stacking-container">
            <div class="discount-rule-editor__grid">
              <div class="discount-rule-editor__grid__column">
                <Input
                  :model="selectedRule.stacking.stackingBehavior"
                  :inputData="getStackingInput('stacking.stackingBehavior')"
                  :isEdit="isEdit"
                  @updateModel="(event) => setValue('stacking.stackingBehavior', event)"
                />
                <Input
                  :model="getMultipleApplicationsModel()"
                  :inputData="multipleApplicationsInput"
                  :isEdit="isEdit"
                  @updateModel="(event) => setMultipleApplications(event)"
                />
              </div>
              <div class="discount-rule-editor__grid__column">
                <div v-if="selectedRule.stacking.stackingBehavior === 'custom'" class="discount-rule-editor__checkbox-group">
                  <label class="discount-rule-editor__checkbox-label">Exclude Discount Types</label>
                  <div class="discount-rule-editor__checkbox-options">
                    <Input
                      v-for="opt in getExcludeTypeInputs()"
                      :key="opt.id"
                      :model="selectedRule.stacking.excludeTypes && selectedRule.stacking.excludeTypes.includes(opt.value)"
                      :inputData="opt"
                      :isEdit="isEdit"
                      @updateModel="toggleExcludeType(opt.value)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </EditorSection> -->

        <!-- Usage Limits Section -->
        <EditorSection title="Usage Limits" subtitle="Set caps on how many times this discount can be used per order, per customer, or globally." sectionType="limits" :bus="bus" :forceCollapsed="enforceMso">
          <div class="discount-rule-editor__limits-container">
            <div class="discount-rule-editor__grid">
              <div class="discount-rule-editor__grid__column">
                <Input
                  :model="selectedRule.usageLimits.maxPerOrder"
                  :inputData="getUsageLimitInput('usageLimits.maxPerOrder')"
                  :isEdit="isFieldEditable"
                  @updateModel="(event) => setValue('usageLimits.maxPerOrder', event)"
                />
                <Input
                  :model="selectedRule.usageLimits.maxPerCustomer"
                  :inputData="getUsageLimitInput('usageLimits.maxPerCustomer')"
                  :isEdit="isFieldEditable"
                  @updateModel="(event) => setValue('usageLimits.maxPerCustomer', event)"
                />
              </div>
              <div class="discount-rule-editor__grid__column">
                <Input
                  :model="selectedRule.usageLimits.maxPerCustomerPerDay"
                  :inputData="getUsageLimitInput('usageLimits.maxPerCustomerPerDay')"
                  :isEdit="isFieldEditable"
                  @updateModel="(event) => setValue('usageLimits.maxPerCustomerPerDay', event)"
                />
                <Input
                  :model="selectedRule.usageLimits.maxTotalUses"
                  :inputData="getUsageLimitInput('usageLimits.maxTotalUses')"
                  :isEdit="isFieldEditable"
                  @updateModel="(event) => setValue('usageLimits.maxTotalUses', event)"
                />
              </div>
            </div>
          </div>
        </EditorSection>

        <!-- Tags Section -->
        <EditorSection title="Tags" subtitle="Add custom tags for categorization, filtering, and reporting." sectionType="tags" :bus="bus">
          <div class="discount-rule-editor__tags-container">
            <div v-if="isEdit" class="discount-rule-editor__tag-input-wrapper">
              <input
                type="text"
                v-model="newTagName"
                class="discount-rule-editor__tag-input"
                placeholder="Type a tag name and press Enter"
                @keydown.enter.prevent="addTag"
              />
              <button
                type="button"
                class="discount-rule-editor__tag-add-btn"
                @click="addTag"
                :disabled="!newTagName.trim()"
              >
                Add
              </button>
            </div>
            <div class="discount-rule-editor__tags-list">
              <span
                v-for="(tag, index) in (selectedRule.tags || [])"
                :key="tag.name + '_' + index"
                class="discount-rule-editor__tag"
              >
                {{ tag.name }}
                <button
                  v-if="isEdit"
                  type="button"
                  class="discount-rule-editor__tag-remove"
                  @click="removeTag(tag.name)"
                >
                  &times;
                </button>
              </span>
              <span v-if="!selectedRule.tags || selectedRule.tags.length === 0" class="discount-rule-editor__no-tags">
                No tags added
              </span>
            </div>
          </div>
        </EditorSection>

        <!-- About Section -->
        <EditorSection
          :title="`About ${selectedRule.name || ''}`"
          sectionType="about"
          v-if="selectedRule._id !== 'NEW'"
          :bus="bus"
        >
          <About :entity="selectedRule" />
        </EditorSection>
      </div>
    </div>
    <SearchModal
        v-if="showSearchModal"
        :showSearchModal="showSearchModal"
        @toggle-modal="() => (showSearchModal = !showSearchModal)"
        :entity="entity"
        :forcedFilterCriteria="filterCriteria"
        :columnHeaders="columnHeaders"
      />

      <DiscountRulePreview
        v-if="showPreviewModal"
        :rule="selectedRule"
        @close="showPreviewModal = false"
      />
  </div>
</template>

<script>
import Vue from "vue";
import PageHeader from "@/components/PageHeader.vue";
import Input from "@/components/Input/Input.vue";
import About from "@/components/About/About.vue";
import EditorSection from "@/components/EditorSection";
import ContainerLabel from "@/components/ContainerLabel/ContainerLabel.vue";
import ConditionBuilder from "./components/ConditionBuilder.vue";
import EffectBuilder from "./components/EffectBuilder.vue";
import DiscountRulePreview from "./components/DiscountRulePreview.vue";
import BaseBtn from "@/components/Base/Button/Button.vue";
import { mapActions, mapGetters } from "vuex";
import Constants from "@/resources/Constants";
import _ from "lodash";
import Utilities from "@/lib/Utilities";
import {
  discountRuleInfoInputs,
  scheduleInputs,
  triggerInputs,
  stackingInputs,
  usageLimitInputs,
  defaultSelectedDiscountRule,
  pageHeader,
  filterCriteria,
  columnHeaders,
  sourceTypeOptions,
  dayOfWeekOptions,
  discountTypeOptions,
  customerConditionFieldOptions,
  multipleApplicationsInput,
  multipleApplicationsOptions,
  defaultBundleGroup,
} from "./inputs";
import {searchColumnHeaders} from "../MSODiscountRule/inputs";
import Entities from "@/resources/Entities";
import LocalStorageManager from "@/lib/LocalStorageManager";

export default {
  name: "discountRuleEditor",
  data() {
    return {
      Constants,
      Utilities,
      discountRuleInfoInputs,
      scheduleInputs,
      triggerInputs,
      stackingInputs,
      usageLimitInputs,
      isEdit: false,
      defaultSelectedDiscountRule,
      bus: new Vue(),
      isCollapsed: true,
      entity: Entities.DISCOUNTRULE,
      pageHeader,
      showSearchModal: false,
      showPreviewModal: false,
      filterCriteria,
      columnHeaders,
      sourceTypeOptions,
      dayOfWeekOptions,
      discountTypeOptions,
      multipleApplicationsInput,
      multipleApplicationsOptions,
      searchColumnHeaders,
      enforceMso: false,
      newTagName: "",
      defaultBundleGroup,
    };
  },
  components: {
    PageHeader,
    Input,
    EditorSection,
    About,
    ContainerLabel,
    ConditionBuilder,
    EffectBuilder,
    DiscountRulePreview,
    BaseBtn,
  },
  mounted: function () {
    this.setPageTitle("Discount Rule");
    const id = this.$route.query.id;
    if (id && id != "NEW") {
      this.getDiscountRuleById({ _id: id });
    } else {
      this.isEdit = true;
    }
    this.forceToggle();
    const organizationId =
      LocalStorageManager.getLocalStorageKey("organizationId");
    this.getOrganizationById({
      _id: organizationId,
    });
  },
  beforeRouteLeave(to, from, next) {
    if (this.isEdit) {
      this.setMessagePopUp({
        isOpen: true,
        title: Constants.strings.unsavedChangesTitle,
        message: Constants.strings.unsavedChangesConfirmation,
        positiveCallback: () => {
          next(true);
        },
      });
    } else {
      next(true);
    }
  },
  watch: {
    organization: function (organization) {
      this.enforceMso = organization?.enforceMso;
    },
    id: function () {
      if (this.id && this.id !== "NEW") {
        this.getDiscountRuleById({
          _id: this.id,
        });
        document.getElementById("app-body").scrollTo(0, 0);
      }
    },
  },
  computed: {
    id: {
      get() {
        return this.$route.query.id;
      },
      set(id) {
        return id;
      },
    },
    ...mapGetters({
      discountRule: "discountRuleModule/discountRule",
      organization: "organizationModule/organization",
      currentStore: "storeModule/currentStore",
    }),
    filteredCustomerConditionFieldOptions() {
      // Only show Loyalty Points option if store uses dispense-first loyalty system
      if (this.currentStore?.loyaltySystem === 'dispense-first') {
        return customerConditionFieldOptions;
      }
      return customerConditionFieldOptions.filter(opt => opt.code !== 'customer.loyaltyPoints');
    },
    // When enforceMso is true, only schedule fields (date, time, days) are editable
    isFieldEditable() {
      return this.enforceMso ? false : this.isEdit;
    },
    selectedRule: {
      get() {
        this.setPageTitle("Discount Rule - " + this.discountRule.name);
        // Ensure nested objects exist
        return this.prefillEntityBeforeRendering(this.discountRule);
      },
      set(entity) {
        return entity;
      },
    },
  },
  methods: {
    ...mapActions({
      getDiscountRuleById: "discountRuleModule/getDiscountRuleById",
      saveDiscountRule: "discountRuleModule/saveDiscountRule",
      deleteDiscountRule: "discountRuleModule/deleteDiscountRule",
      resetUsages: "discountRuleModule/resetUsages",
      getOrganizationById: "organizationModule/getOrganizationById",
      setMessagePopUp: "setMessagePopUp",
      setMessage: "setMessage",
    }),

    getInput(field) {
      return discountRuleInfoInputs.find((i) => i.id === field);
    },

    getSourceTypeInput() {
      return sourceTypeOptions.map((opt, index) => {
        return {
          id: index,
          type: "checkbox",
          title: opt.label,
          value: opt.code,
        };
      })
    },

    getDayOfWeekInput() {
      return dayOfWeekOptions.map((opt, index) => {
        return {
          id: index,
          type: "checkbox",
          title: opt.label,
          value: opt.code,
        };
      })
    },
    getScheduleInput(field) {
      return scheduleInputs.find((i) => i.id === field);
    },

    getTriggerInput(field) {
      return triggerInputs.find((i) => i.id === field);
    },

    getStackingInput(field) {
      return stackingInputs.find((i) => i.id === field);
    },

    getExcludeTypeInputs() {
      return discountTypeOptions.map((opt, index) => ({
        id: `excludeType-${index}`,
        type: "checkbox",
        title: opt.label,
        value: opt.code,
      }));
    },

    getUsageLimitInput(field) {
      return usageLimitInputs.find((i) => i.id === field);
    },

    forceToggle() {
      this.isCollapsed = !this.isCollapsed;
      this.bus.$emit("forceToggle", this.isCollapsed);
    },

    toggleEdit() {
      if (!(this.selectedRule._id === "NEW")) this.isEdit = !this.isEdit;
      if (this.selectedRule._id === "NEW") {
        this.isEdit = true;
      }
    },

    handleAdjacent: async function (mode) {
      // Adjacent navigation could be implemented here
    },

    toggleSearchModal: function () {
      this.showSearchModal = !this.showSearchModal;
    },

    menuOptionClick(entity, key) {
      if (key === "duplicate") {
        this.$store.dispatch("discountRuleModule/duplicateRule", {
          _id: this.selectedRule._id,
          positiveCallback: (newRule) => {
            this.$router.push({ name: "discountRuleEditor", query: { id: newRule._id } });
          },
        });
      } else if (key === "preview") {
        this.$router.push({ name: "discountRulePreview" });
      }
    },

    toggleSourceType(value) {
      if (!this.selectedRule.sourceType) {
        this.selectedRule.sourceType = [];
      }
      const index = this.selectedRule.sourceType.indexOf(value);
      if (index > -1) {
        this.selectedRule.sourceType.splice(index, 1);
      } else {
        this.selectedRule.sourceType.push(value);
      }
      this.setValue('sourceType', this.selectedRule.sourceType);
    },

    toggleDayOfWeek(value) {
      if (!this.selectedRule.daysOfWeek) {
        this.selectedRule.daysOfWeek = [];
      }
      const index = this.selectedRule.daysOfWeek.indexOf(value);
      if (index > -1) {
        this.selectedRule.daysOfWeek.splice(index, 1);
      } else {
        this.selectedRule.daysOfWeek.push(value);
      }
      this.setValue('daysOfWeek', this.selectedRule.daysOfWeek);
    },

    toggleExcludeType(value) {
      if (!this.selectedRule.stacking.excludeTypes) {
        this.selectedRule.stacking.excludeTypes = [];
      }
      const index = this.selectedRule.stacking.excludeTypes.indexOf(value);
      if (index > -1) {
        this.selectedRule.stacking.excludeTypes.splice(index, 1);
      } else {
        this.selectedRule.stacking.excludeTypes.push(value);
      }
      this.setValue('stacking.excludeTypes', this.selectedRule.stacking.excludeTypes);
    },

    setValue(field, value) {
      _.set(this.discountRule, field, value);
      this.$forceUpdate();
    },

    updateCustomerConditions(conditions) {
      this.$set(this.discountRule.triggers, 'customerConditions', conditions);
    },

    getMultipleApplicationsModel() {
      const value = this.selectedRule.allowMultipleApplications;
      return this.multipleApplicationsOptions.find(opt => opt.code === value) || this.multipleApplicationsOptions[0];
    },

    setMultipleApplications(event) {
      const value = event && event.code !== undefined ? event.code : null;
      this.setValue('allowMultipleApplications', value);
    },

    prefillEntityBeforeRendering(discountRule) {
      if (!discountRule.daysOfWeek) {
        discountRule.daysOfWeek = [];
      }
      if (!discountRule.schedule) {
        discountRule.schedule = { daysOfWeek: [], timeWindows: [] };
      }
      if (!discountRule.triggers) {
        discountRule.triggers = { minQuantity: 0, minSpend: 0, minCannabisGrams: 0, customerConditions: { type: "AND", rules: [] } };
      }
      if (!discountRule.conditions) {
        discountRule.conditions = { type: "AND", rules: [] };
      }
      if (!discountRule.effects) {
        discountRule.effects = [];
      }
      if (!discountRule.stacking) {
        discountRule.stacking = { stackingBehavior: "default", excludeTypes: [], excludeRuleIds: [], onlyStackWithTypes: [] };
      }
      if (!discountRule.usageLimits) {
        discountRule.usageLimits = {};
      }
      if (!discountRule.tags) {
        discountRule.tags = [];
      }
      if (!discountRule.bundleGroups) {
        discountRule.bundleGroups = [];
      }
      if(!discountRule._id) {
        discountRule._id = "NEW";
      }
      return discountRule;
    },

    addTag() {
      const tagName = this.newTagName.trim();
      if (!tagName) return;

      if (!this.selectedRule.tags) {
        this.selectedRule.tags = [];
      }

      // Check for duplicate tag names
      const exists = this.selectedRule.tags.some(t => t.name.toLowerCase() === tagName.toLowerCase());
      if (exists) {
        this.setMessage({ text: "Tag already exists", type: "error" });
        return;
      }

      // Tags only have a name field (no _id - schema uses _id: false)
      const newTag = { name: tagName };

      this.selectedRule.tags.push(newTag);
      this.setValue('tags', this.selectedRule.tags);
      this.newTagName = "";
    },

    removeTag(tagName) {
      if (!this.selectedRule.tags) return;

      const index = this.selectedRule.tags.findIndex(t => t.name === tagName);
      if (index > -1) {
        this.selectedRule.tags.splice(index, 1);
        this.setValue('tags', this.selectedRule.tags);
      }
    },

    addBundleGroup() {
      if (!this.selectedRule.bundleGroups) {
        this.selectedRule.bundleGroups = [];
      }
      // Create a deep copy of the default bundle group
      const newGroup = _.cloneDeep(this.defaultBundleGroup);
      this.selectedRule.bundleGroups.push(newGroup);
      this.setValue('bundleGroups', this.selectedRule.bundleGroups);
    },

    removeBundleGroup(index) {
      if (!this.selectedRule.bundleGroups) return;
      this.selectedRule.bundleGroups.splice(index, 1);
      this.setValue('bundleGroups', this.selectedRule.bundleGroups);
    },

    updateBundleGroupField(groupIndex, field, value) {
      if (!this.selectedRule.bundleGroups || !this.selectedRule.bundleGroups[groupIndex]) return;
      this.$set(this.selectedRule.bundleGroups[groupIndex], field, value);
      this.setValue('bundleGroups', this.selectedRule.bundleGroups);
    },

    updateBundleGroupConditions(groupIndex, conditions) {
      if (!this.selectedRule.bundleGroups || !this.selectedRule.bundleGroups[groupIndex]) return;
      this.$set(this.selectedRule.bundleGroups[groupIndex], 'conditions', conditions);
      this.setValue('bundleGroups', this.selectedRule.bundleGroups);
    },

    selectItemFromSearch: function (entity, item) {
      if (entity === Entities.MSODISCOUNTRULE && item) {
        // Copy fields from MSO template to current rule
        const fieldsToCopy = [
          'name', 'description', 'code',
          'onlineStoreName',
          'discountType', 'saleType', 'sourceType',
          'isActive', 'startDateTime', 'endDateTime',
          'daysOfWeek', 'schedule', 'usageLimits',
          'conditions', 'triggers', 'effects',
          'stacking', 'allowMultipleApplications', 'priority',
          'tags', 'bundleGroups'
        ];
        fieldsToCopy.forEach(field => {
          if (item[field] !== undefined) {
            this.setValue(field, _.cloneDeep(item[field]));
          }
        });
        // Store reference to MSO rule
        this.setValue('msoDiscountRule', item);
      }
    },

    confirmResetUsages() {
      this.setMessagePopUp({
        isOpen: true,
        title: "Reset Usages",
        message: "This will reset all global and per-customer usage tracking for this discount rule. This action cannot be undone. Continue?",
        positiveCallback: () => {
          this.resetUsages({
            discountRuleId: this.selectedRule._id,
            positiveCallback: (result) => {
              this.setMessage({
                text: `Usages reset: ${result.customerUsagesDeleted} customer record(s) cleared`,
                type: "success"
              });
            },
          });
        },
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import "./_discountRuleEditor.scss";
</style>

